import React, { useEffect, useMemo, useState } from "react";
import { resolveParamTokens } from "@/lib/paramEngine";
import api from "@/lib/api";
import { formatNumber } from "@/lib/chartTheme";

const cleanF = (filters) => (filters || [])
  .filter((f) => f && f.field && f.value != null && f.value !== "" && !(Array.isArray(f.value) && !f.value.length))
  .map(({ __src, dataset_id, key, ...r }) => r);

async function q(datasetId, publicToken, body) {
  if (publicToken) {
    const { data } = await api.post("/public/model/query", { token: publicToken, dataset_id: datasetId, semantic_query: body }, { timeout: 45000 });
    return data;
  }
  const { data } = await api.post(`/datasets/${datasetId}/model/query`, body, { timeout: 45000 });
  return data;
}

// Dynamic text widget: {{name}} tokens resolved from live queries, with optional
// period-over-period comparisons ({{name.prev}}, {{name.delta}}, {{name.delta_pct}}).
export function DynamicTextView({ config, datasetId, globalFilters = [], publicToken = null, render, params = null }) {
  const ts = config.textStyle || {};
  const vals = useMemo(() => (config.dynValues || []).filter((v) => v && v.name && v.field), [config.dynValues]);
  const loc = config.options?.locale || undefined;
  const filters = useMemo(() => cleanF([...(config.filters || []), ...(globalFilters || [])]), [config.filters, globalFilters]);
  const key = JSON.stringify({ d: datasetId, v: vals, f: filters, p: publicToken || null });
  const [res, setRes] = useState({});

  useEffect(() => {
    if (!vals.length || !datasetId) { setRes({}); return; }
    let active = true;
    (async () => {
      const out = {};
      const simple = vals.filter((v) => !(v.period && v.dateField));
      const periodic = vals.filter((v) => v.period && v.dateField);
      try {
        if (simple.length) {
          const d = await q(datasetId, publicToken, {
            dimensions: [], measures: [], sort: [], limit: 1, filters,
            adhoc_measures: simple.map((v, i) => ({ field: v.field, agg: v.agg || "sum", alias: `dv_${i}` })),
          });
          const r0 = d?.rows?.[0] || {};
          simple.forEach((v, i) => { out[v.name] = { cur: Number(r0[`dv_${i}`]) }; });
        }
        for (const v of periodic) {
          const outName = `${v.dateField}_${v.period}`;
          const d = await q(datasetId, publicToken, {
            dimensions: [{ field: v.dateField, date_part: v.period }], measures: [], sort: [], limit: 5000, filters,
            adhoc_measures: [{ field: v.field, agg: v.agg || "sum", alias: "dv" }],
          });
          const rows = (d?.rows || [])
            .filter((r) => r[outName] != null && String(r[outName]) !== "NaT")
            .sort((a, b) => String(a[outName]).localeCompare(String(b[outName])));
          const cur = Number(rows[rows.length - 1]?.dv);
          const prev = Number(rows[rows.length - 2]?.dv);
          const delta = Number.isFinite(cur) && Number.isFinite(prev) ? cur - prev : null;
          const delta_pct = delta != null && prev !== 0 ? (delta / Math.abs(prev)) * 100 : null;
          out[v.name] = { cur, prev, delta, delta_pct, periodLabel: rows[rows.length - 1]?.[outName], prevLabel: rows[rows.length - 2]?.[outName] };
        }
      } catch (e) { console.debug("dynamic text query failed", e); }
      if (active) setRes(out);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const colorDeltas = config.options?.deltaColors !== false;
  const text = useMemo(() => {
    const src = resolveParamTokens(config.text || "", params);
    if (!vals.length) return src;
    // Deltas are wrapped in {green:…}/{red:…} markers rendered as colored spans.
    const colorize = (s, v) => (!colorDeltas ? s : v > 0 ? `{green:${s}}` : v < 0 ? `{red:${s}}` : `{gray:${s}}`);
    return src.replace(/\{\{\s*(\w+)(?:\.(\w+))?\s*\}\}/g, (m0, name, part) => {
      const r = res[name];
      if (!r) return "…";
      const fmt = (n) => (Number.isFinite(n) ? formatNumber(n, loc) : "—");
      if (!part) return fmt(r.cur);
      if (part === "prev") return fmt(r.prev);
      if (part === "delta") return Number.isFinite(r.delta) ? colorize(`${r.delta >= 0 ? "+" : "−"}${formatNumber(Math.abs(r.delta), loc)}`, r.delta) : "—";
      if (part === "delta_pct" || part === "pct") return Number.isFinite(r.delta_pct) ? colorize(`${r.delta_pct >= 0 ? "+" : "−"}${Math.abs(r.delta_pct).toFixed(1)}%`, r.delta_pct) : "—";
      if (part === "period") return r.periodLabel ?? "—";
      if (part === "prev_period") return r.prevLabel ?? "—";
      return fmt(r.cur);
    });
  }, [config.text, res, vals.length, loc, colorDeltas, params]);

  return (
    <div className="h-full w-full overflow-auto p-2" data-testid={vals.length ? "dynamic-text-widget" : "text-widget"}
      style={ts.bg ? { backgroundColor: ts.bg, borderRadius: 10 } : undefined}>
      <div className="text-[color:var(--ink-2)] leading-relaxed"
        style={{
          fontSize: ts.size ? `${ts.size}px` : "14px",
          textAlign: ts.align || "left",
          color: ts.color || undefined,
          fontWeight: ts.bold ? 700 : undefined,
          fontStyle: ts.italic ? "italic" : undefined,
          textDecoration: ts.underline ? "underline" : undefined,
          fontFamily: ts.fontFamily || undefined,
        }}>
        {render(text || "Double-click to edit this note.")}
      </div>
    </div>
  );
}
